import React from 'react';
import styles from '../AssignedJobsSection.module.css';
import { fmtHrs, fmtUSD, daysUntil, jobHoursTarget, taskUrl, robotUrl, paymentState } from './jobsApi';

// ── Task chips (job table cell) ─────────────────────────────────────────────

export const ChipExtIcon = () => (
    <svg className={styles.chipExt} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M5 2.5H2.5v7h7V7" />
        <path d="M7.5 2.5h2v2M9.5 2.5L5.5 6.5" />
    </svg>
);

export const TaskChips = ({ tasks }) => {
    const shown = (tasks || []).slice(0, 2);
    const rest = (tasks || []).length - shown.length;
    return (
        <div className={styles.jobTasks}>
            {shown.map((t) => (
                <a
                    key={t.task_id}
                    className={`${styles.jobChip} ${styles.jobChipLink}`}
                    href={taskUrl(t.task_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open the ${t.task_name} upload page`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {t.task_name}
                    <span className={styles.chipHrs}>{fmtHrs(t.hours_logged)}h / {fmtHrs(t.assigned_hours)}h</span>
                    <ChipExtIcon />
                </a>
            ))}
            {rest > 0 && <span className={styles.jobMore}>+{rest} more</span>}
        </div>
    );
};

// ── Progress / Amount / Due cells ───────────────────────────────────────────

export const ProgressCell = ({ job }) => {
    const target = jobHoursTarget(job);
    const logged = Number(job.hours_logged || 0);
    const pct = target > 0 ? Math.min(100, Math.round((logged / target) * 100)) : 0;
    const done = target > 0 && logged >= target;
    const left = daysUntil(job.due_date);
    const late = !done && left != null && left < 0;
    const color = done ? '#4caf7d' : late ? '#e05c5c' : '#5a9fd4';
    return (
        <div className={styles.progWrap}>
            <div className={styles.progCounts}>
                <span className={styles.progHrs}>{fmtHrs(logged)} / {fmtHrs(target)} h</span>
                <span className={styles.progPct} style={done ? { color: '#4caf7d' } : late ? { color: '#e05c5c' } : undefined}>
                    {done ? 'Complete' : `${pct}%`}
                </span>
            </div>
            <div className={styles.progBar}>
                <div className={styles.progFill} style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
};

export const AmountCell = ({ job }) => {
    const p = paymentState(job);
    const earned = Number(job.amount_earned_total || 0);
    return (
        <div className={styles.payWrap}>
            <span className={styles.paidAmt} style={earned === 0 ? { color: '#555' } : undefined}>{fmtUSD(earned)}</span>
            <span className={styles.payStatus} style={{ color: p.color }}>
                <span className={styles.payDot} style={{ background: p.key === 'none' ? 'rgba(255,255,255,0.14)' : p.color }} />
                {p.label}
            </span>
        </div>
    );
};

export const DueCell = ({ dueDate }) => {
    const left = daysUntil(dueDate);
    let cls = styles.dueOk;
    let txt = `${left} days left`;
    if (left == null) { cls = styles.dueOk; txt = '—'; }
    else if (left < 0) { cls = styles.dueLate; txt = `Overdue by ${-left} day${left === -1 ? '' : 's'}`; }
    else if (left === 0) { cls = styles.dueSoon; txt = 'Due today'; }
    else if (left <= 7) { cls = styles.dueSoon; txt = `${left} day${left === 1 ? '' : 's'} left`; }
    return (
        <div className={styles.dueWrap}>
            <span className={styles.dueDate}>{dueDate || '—'}</span>
            <span className={`${styles.dueSub} ${cls}`}>{txt}</span>
        </div>
    );
};

// ── Robot card (job form + drawer) ──────────────────────────────────────────
// An order is fulfilled on exactly one robot platform, so the robot is a
// property of the order, not a per-job choice.

export const RobotIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
        <rect x="3" y="5.5" width="10" height="7.5" rx="2" />
        <path d="M8 5.5V3M8 3a1 1 0 100-2 1 1 0 000 2z" />
        <path d="M6 8.5v1M10 8.5v1M1.5 8.5v2M14.5 8.5v2" />
    </svg>
);

export const RobotCard = ({ robot, hasOrder }) => {
    if (!hasOrder) {
        return <div className={styles.robotCardEmpty}>Pick an order to see the robot it runs on.</div>;
    }
    if (!robot) {
        return <div className={styles.robotCardEmpty}>No robot associated with this order.</div>;
    }
    const meta = [robot.category, robot.degree_of_freedom ? `${robot.degree_of_freedom}-DoF` : null].filter(Boolean).join(' · ');
    return (
        <div className={styles.robotCard}>
            <div className={styles.robotIco}><RobotIcon /></div>
            <div className={styles.robotInfo}>
                <div className={styles.robotName}>{robot.product_name}</div>
                {(robot.manufacture || meta) && (
                    <div className={styles.robotMeta}>{[robot.manufacture, meta].filter(Boolean).join(' · ')}</div>
                )}
            </div>
            <a className={styles.robotLink} href={robotUrl(robot.machine_id)} target="_blank" rel="noopener noreferrer">
                Spec ↗
            </a>
        </div>
    );
};
